/**
 * Minimal markdown parser for chat messages
 */

/**
 * Parse markdown text to HTML
 * @param {string} text - The markdown text
 * @returns {string} The HTML string
 */
export function parseMarkdown(text) {
    if (!text) return '';
    
    let html = text;
    
    // Escape HTML first to prevent XSS
    html = escapeHtml(html);
    
    // Code blocks (must be processed before inline code)
    html = html.replace(/```(\w+)?\n([\s\S]*?)\n```/g, (match, lang, code) => {
        const language = lang ? ` class="language-${lang}"` : '';
        return `<pre><code${language}>${code.trim()}</code></pre>`;
    });
    
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    
    // Bold text
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // Italic text
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Unordered lists
    html = html.replace(/^[\s]*[-*+][\s]+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    
    // Ordered lists
    html = html.replace(/^[\s]*\d+\.[\s]+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, (match) => {
        // Only wrap in <ol> if not already wrapped in <ul>
        if (!match.includes('<ul>')) {
            return `<ol>${match}</ol>`;
        }
        return match;
    });
    
    // Line breaks
    html = html.replace(/\n/g, '<br>');
    
    // Clean up multiple <br> tags
    html = html.replace(/(<br>\s*){3,}/g, '<br><br>');
    
    return html;
}

/**
 * Escape HTML characters to prevent XSS
 * @param {string} text - The text to escape
 * @returns {string} The escaped text
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Extract plain text from markdown
 * @param {string} markdown - The markdown text
 * @returns {string} Plain text without markdown formatting
 */
export function extractPlainText(markdown) {
    if (!markdown) return '';
    
    let text = markdown;
    
    // Remove code blocks
    text = text.replace(/```[\s\S]*?```/g, '[code block]');
    
    // Remove inline code
    text = text.replace(/`[^`]+`/g, '[code]');
    
    // Remove bold/italic
    text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
    text = text.replace(/\*([^*]+)\*/g, '$1');
    
    // Remove links
    text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Remove list markers
    text = text.replace(/^[\s]*[-*+\d\.]\s+/gm, '');
    
    // Clean up whitespace
    text = text.replace(/\n+/g, ' ').trim();
    
    return text;
}

/**
 * Check if text contains markdown formatting
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains markdown
 */
export function hasMarkdown(text) {
    if (!text) return false;
    
    const markdownPatterns = [
        /```[\s\S]*?```/,  // Code blocks
        /`[^`]+`/,         // Inline code
        /\*\*[^*]+\*\*/,   // Bold
        /\*[^*]+\*/,       // Italic
        /\[[^\]]+\]\([^)]+\)/, // Links
        /^[\s]*[-*+]\s+/m, // Unordered lists
        /^[\s]*\d+\.\s+/m  // Ordered lists
    ];
    
    return markdownPatterns.some(pattern => pattern.test(text));
}
